import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";

const TW = 380;

export default function PortalTour({ steps, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  const step = steps[i];
  const last = i === steps.length - 1;
  const isMobile = vw < 640;

  // Se si apre un altro modale (Dialog/AlertDialog Radix), chiudo la guida così non copre le finestre.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const check = () => {
      const dlg = document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
      if (dlg) closeRef.current?.();
    };
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    check();
    return () => obs.disconnect();
  }, []);

  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.selector}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    step?.action?.();
    const mobile = window.innerWidth < 640;
    const focus = () => {
      const el = step?.selector ? document.querySelector(`[data-testid="${step.selector}"]`) : null;
      if (el) {
        el.scrollIntoView({ block: mobile ? "start" : "center", behavior: "smooth" });
        // Su mobile il tooltip è un pannello in basso: lascio un margine sopra così non copre il contenuto evidenziato.
        if (mobile) setTimeout(() => window.scrollBy({ top: -90, behavior: "smooth" }), 60);
      }
      measure();
    };
    // Re-misura più volte: alcune sezioni caricano i dati (es. demo) in modo asincrono.
    const timers = [setTimeout(focus, 180), setTimeout(focus, 520), setTimeout(measure, 950)];
    return () => timers.forEach(clearTimeout);
  }, [i, step, measure]);

  useEffect(() => {
    const on = () => { setVw(window.innerWidth); measure(); };
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [measure]);

  const finish = () => onClose?.();
  const next = () => (last ? finish() : setI(i + 1));
  const prev = () => setI(Math.max(0, i - 1));

  const pad = 8;
  const hole = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;

  // Larghezza tooltip: piena (con margini) su mobile, fissa su desktop
  const tipW = isMobile ? Math.min(vw - 24, 480) : TW;

  // Posizione del tooltip
  let tipLeft, tipTop, centered = false;
  if (isMobile) {
    // Su mobile il tooltip è un pannello ancorato in basso a tutta larghezza (bottom sheet)
    centered = false;
  } else if (hole) {
    tipLeft = Math.max(12, Math.min(hole.left + hole.width / 2 - tipW / 2, window.innerWidth - tipW - 12));
    const below = hole.top + hole.height + 14;
    const wouldOverflow = below + 260 > window.innerHeight;
    tipTop = wouldOverflow ? Math.max(12, hole.top - 260) : below;
  } else {
    centered = true;
  }

  const tipStyle = isMobile
    ? { left: 12, right: 12, bottom: 16, width: "auto", maxWidth: 480, marginLeft: "auto", marginRight: "auto" }
    : centered
      ? { width: tipW, left: "50%", top: "50%", transform: "translate(-50%,-50%)" }
      : { width: tipW, left: tipLeft, top: tipTop };

  const Tip = (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: isMobile ? 24 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed z-[210] rounded-2xl bg-white dark:bg-zinc-900 border border-border shadow-2xl p-5 text-left pointer-events-auto"
      style={tipStyle}
      data-testid="portal-tour-tip"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-lg leading-snug text-foreground pr-2">{step.title}</h3>
        <button onClick={finish} data-testid="portal-tour-skip" className="text-muted-foreground hover:text-foreground shrink-0"><X className="h-5 w-5" /></button>
      </div>
      <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{step.body}</p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex gap-1" aria-hidden="true">
          {steps.map((_, k) => (
            <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-4 bg-[#007BFF]" : "w-1.5 bg-border"}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {i > 0 && (
            <button onClick={prev} data-testid="portal-tour-prev" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground px-2 py-1.5">
              <ArrowLeft className="h-3.5 w-3.5" />Indietro
            </button>
          )}
          <button onClick={next} data-testid="portal-tour-next" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-full px-3.5 py-1.5">
            {last ? <>Fine<Check className="h-3.5 w-3.5" /></> : <>Avanti<ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
        </div>
      </div>
    </motion.div>
  );

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] pointer-events-none" data-testid="portal-tour">
        {hole ? (
          <motion.div
            initial={false}
            animate={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="absolute rounded-xl pointer-events-none"
            style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.65)", border: "2px solid #007BFF" }}
          />
        ) : (
          <div className="absolute inset-0 bg-black/65" />
        )}
        <AnimatePresence mode="wait">{Tip}</AnimatePresence>
      </div>
    </AnimatePresence>,
    document.body
  );
}
